<script setup lang="ts">
import { nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'

interface Props {
  image: string
  imageAlt: string
  title: string
  description: string
  ctaLabel: string
  ctaHref?: string
}

const props = defineProps<Props>()

const emit = defineEmits<{ (e: 'ctaClick'): void }>()

const HEADING_MAX_LINES = 5

const panelRef = ref<HTMLElement | null>(null)
const headingRef = ref<HTMLElement | null>(null)

const headingClamp = ref(HEADING_MAX_LINES)

function countNaturalLines(el: HTMLElement | null): number {
  if (!el)
    return 0

  const clone = el.cloneNode(true) as HTMLElement
  clone.style.position = 'absolute'
  clone.style.visibility = 'hidden'
  clone.style.pointerEvents = 'none'
  clone.style.height = 'auto'
  clone.style.width = `${el.clientWidth}px`
  clone.style.webkitLineClamp = 'unset'
  clone.style.overflow = 'visible'
  el.parentElement?.appendChild(clone)

  const lineHeight = Number.parseFloat(getComputedStyle(el).lineHeight)
  const lines = Math.ceil(clone.scrollHeight / lineHeight)
  clone.remove()

  return lines
}

function recalcClamp() {
  headingClamp.value = countNaturalLines(headingRef.value) || HEADING_MAX_LINES
}

let resizeObserver: ResizeObserver | null = null

onMounted(() => {
  nextTick(recalcClamp)
  resizeObserver = new ResizeObserver(() => recalcClamp())
  if (panelRef.value)
    resizeObserver.observe(panelRef.value)
})

onBeforeUnmount(() => {
  resizeObserver?.disconnect()
})

watch(() => props.title, () => nextTick(recalcClamp))
</script>

<template>
  <div class="relative w-full h-full">
    <SharedSmoothImage :src="image" :alt="imageAlt" class="absolute inset-0 w-full h-full" />

    <div
      ref="panelRef"
      class="absolute inset-0 w-[60%] flex flex-col justify-center gap-2 p-4xs pl-xl bg-[rgba(239,255,193,0.95)]"
    >
      <h2
        ref="headingRef"
        class="heading-clamp text-brand-primary-pure font-highlight text-2xs lg:system:text-sm font-[500] leading-xs"
        :style="{ '--clamp-lines': headingClamp }"
      >
        {{ title }}
      </h2>

      <a
        v-if="ctaHref"
        :href="ctaHref"
        class="self-start py-nano px-4xs rounded-md bg-neutral-low-pure text-neutral-high-pure text-micro lg:system:text-3xs font-bold leading-xs no-underline cursor-pointer transition-opacity duration-200 ease-out hover:opacity-[0.85]"
      >
        {{ ctaLabel }}
      </a>
      <button
        v-else
        type="button"
        class="self-start py-nano px-4xs rounded-md border-0 bg-neutral-low-pure text-neutral-high-pure text-micro lg:system:text-3xs font-bold leading-xs cursor-pointer transition-opacity duration-200 ease-out hover:opacity-[0.85]"
        @click="emit('ctaClick')"
      >
        {{ ctaLabel }}
      </button>
    </div>
  </div>
</template>

<style scoped>
.heading-clamp {
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: var(--clamp-lines, 5);
  line-clamp: var(--clamp-lines, 5);
  overflow: hidden;
}
</style>
